import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Input,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react';
import { useLocation } from 'wouter';
import {
  getShipmentCases,
  getMyShipmentCases,
  getShipmentCaseRecord,
  notifyShipmentCasesChanged,
  type CaseCommandResult,
  type CaseScope,
  type ShipmentCase,
} from 'common/src/api/shipment-cases';
import useUserStore from 'common/src/stores/useUserStore';
import useFilterStore from 'common/src/stores/useFiltersStore';
import useDataStore from 'common/src/stores/useDataStore';
import { useTranslation } from 'common/src/hooks/useTranslation';
import Header from '../../components/layouts/Header';
import {
  Alert,
  EmptyState,
  LoadingState,
  PageLayout,
  StatusChip,
} from '../../components/ui';
import { useScopedRequest } from '../shipments/arrival-manager/use-scoped-request';
import {
  caseKinds,
  caseStatuses,
  caseTotals,
  confidenceLabels,
  isCaseOverdue,
  caseLabel,
} from './model';
import NewCaseModal from './NewCaseModal';
import CaseDetailModal from './CaseDetailModal';
import CasesSummary from './CasesSummary';

export default function ShipmentCasesPage() {
  const { userProfile } = useUserStore();
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const { t } = useTranslation('shipmentCases');
  const [location] = useLocation();
  const mine = location === '/claims/my-actions';
  const scope = useMemo<CaseScope | null>(
    () =>
      userProfile?.exporter_id && selectedSeason && selectedSpecies?.id
        ? {
            exporter_id: Number(userProfile.exporter_id),
            season: selectedSeason,
            species_id: Number(selectedSpecies.id),
          }
        : null,
    [userProfile?.exporter_id, selectedSeason, selectedSpecies?.id]
  );
  const identity = JSON.stringify([
    userProfile?.auth_id,
    userProfile?.id,
    scope,
  ]);
  return scope || (mine && userProfile?.exporter_id) ? (
    <CasesWorkspace
      key={`${identity}:${mine}`}
      scope={scope}
      exporterId={Number(userProfile?.exporter_id)}
      identity={identity}
    />
  ) : (
    <PageLayout>
      <Header title={t('Reclamos')} />
      <EmptyState
        title={t(
          'Selecciona una exportadora, temporada y especie para gestionar casos.'
        )}
      />
    </PageLayout>
  );
}
function CasesWorkspace({
  scope,
  exporterId,
  identity,
}: {
  scope: CaseScope | null;
  exporterId: number;
  identity: string;
}) {
  const { t, currentLanguage } = useTranslation('shipmentCases');
  const [newCase, setNewCase] = useState(false);
  const [selected, setSelected] = useState<{
    id: number;
    scope: CaseScope;
  } | null>(null);
  const [location, setLocation] = useLocation();
  const mine = location === '/claims/my-actions';
  const view = mine
    ? 'mine'
    : location === '/claims/details'
      ? 'detail'
      : 'summary';
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  const [status, setStatus] = useState('all');
  const [client, setClient] = useState('all');
  const [responsible, setResponsible] = useState('all');
  const [waiting, setWaiting] = useState('all');
  const [shipment, setShipment] = useState<number | null>(
    () =>
      Number(new URLSearchParams(window.location.search).get('shipment')) ||
      null
  );
  const load = useCallback(
    (signal: AbortSignal) =>
      mine
        ? getMyShipmentCases(exporterId, signal)
        : getShipmentCases(scope!, signal),
    [scope, mine, exporterId]
  );
  const request = useScopedRequest(identity, load);
  const rows = request.data || [];
  // Enlace directo desde los correos de aviso: /claims/details?case=<id>.
  const deepLinkCase = useRef(
    Number(new URLSearchParams(window.location.search).get('case')) || null
  );
  useEffect(() => {
    const id = deepLinkCase.current;
    if (!id || !request.data) return;
    const record = request.data.find((c) => c.id === id);
    if (!record) return;
    deepLinkCase.current = null;
    setSelected({ id: record.id, scope: record });
  }, [request.data]);
  const loadSelected = useCallback(
    (signal: AbortSignal) =>
      getShipmentCaseRecord(selected!.scope, selected!.id, signal),
    [selected]
  );
  const selectedRead = useScopedRequest(
    selected
      ? `${identity}:record:${selected.scope.season}:${selected.scope.species_id}:${selected.id}`
      : null,
    loadSelected
  );
  const today = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const shortDate = (iso?: string | null) =>
    iso
      ? new Intl.DateTimeFormat(currentLanguage === 'en' ? 'en-US' : 'es-CL', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(new Date(`${iso}T00:00:00`))
      : '';
  const filtered = rows.filter(
    (c) =>
      (!shipment || c.shipment_snapshot.id === shipment) &&
      (kind === 'all' || c.kind === kind) &&
      (status === 'all' || c.status === status) &&
      (client === 'all' || (c.shipment_snapshot.receiver || '—') === client) &&
      (waiting === 'all' || c.waiting_parties.includes(waiting)) &&
      (responsible === 'all' ||
        (c.responsible || 'Sin asignar') === responsible) &&
      `${caseLabel(c)} ${c.current_reference} ${c.shipment_snapshot.reference} ${c.shipment_snapshot.container} ${c.shipment_snapshot.receiver} ${c.reasons.join(' ')}`
        .toLowerCase()
        .includes(query.toLowerCase())
  );
  const totals = caseTotals(filtered);
  const selectedRecord = selectedRead.data;
  const money = (n: number) =>
    new Intl.NumberFormat(currentLanguage === 'en' ? 'en-US' : 'es-CL', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(n);
  const filter = (
    label: string,
    value: string,
    set: (v: string) => void,
    choices: Record<string, string>
  ) => (
    <Select
      className='min-w-40 flex-1'
      size='sm'
      label={t(label)}
      selectedKeys={[value]}
      onChange={(e) => set(e.target.value)}
    >
      {Object.entries({ all: 'Todos', ...choices }).map(([k, v]) => (
        <SelectItem key={k}>{t(v)}</SelectItem>
      ))}
    </Select>
  );
  const options = (values: string[]) =>
    Object.fromEntries([...new Set(values)].map((v) => [v, v]));
  function saved(result: CaseCommandResult) {
    setNewCase(false);
    if (selected?.id === result.case_id) void selectedRead.reload();
    else if (scope) setSelected({ id: result.case_id, scope });
    void request.reload();
  }
  function pendingLabel(record: ShipmentCase) {
    if (record.pending_inputs)
      return `${record.pending_inputs} ${t(record.pending_inputs === 1 ? 'solicitud pendiente' : 'solicitudes pendientes')} · ${record.oldest_input?.recipient || '—'} · ${t('vence')} ${shortDate(record.oldest_input?.due_on)}`;
    return record.status === 'closed' ? '—' : t('Sin solicitudes');
  }
  function exportCases() {
    const cells = [
      [
        'Caso',
        'Referencia',
        'Tipo',
        'Estado',
        'Solicitudes pendientes',
        'Kg afectados',
        'Pérdida estimada USD',
        'Confianza',
      ],
      ...filtered.map((c) => [
        caseLabel(c),
        c.current_reference,
        t(caseKinds[c.kind]),
        t(caseStatuses[c.status]),
        c.waiting_parties.join(' · '),
        c.items.reduce((n, i) => n + Number(i.kilos), 0),
        c.valuation?.amount_usd ?? '',
        c.valuation ? t(confidenceLabels[c.valuation.confidence]) : '',
      ]),
    ];
    const csv = cells
      .map((row) =>
        row
          .map(
            (v) =>
              `"${String(v)
                .replace(/"/g, '""')
                .replace(/^[=+@-]/, "'$&")}"`
          )
          .join(',')
      )
      .join('\r\n');
    const url = URL.createObjectURL(
      new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' })
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `reclamos-${mine ? 'mis-gestiones' : scope!.season}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }
  return (
    <PageLayout>
      <Header
        title={`${t('Reclamos')} · ${t(view === 'mine' ? 'Mis gestiones' : view === 'detail' ? 'Detalle' : 'Resumen')}`}
        description={t(
          'Gestiona observaciones, reclamos y siniestros asociados a tus despachos.'
        )}
        actions={
          <div className='flex gap-2'>
            <Button
              variant='light'
              isLoading={request.loading}
              onPress={() => {
                void request.reload();
                notifyShipmentCasesChanged(exporterId);
              }}
            >
              {t('Actualizar')}
            </Button>
            <Button
              variant='flat'
              onPress={exportCases}
              isDisabled={!filtered.length}
            >
              {t('Exportar casos')}
            </Button>
            <Button
              color='primary'
              isDisabled={!scope}
              onPress={() => setNewCase(true)}
            >
              {t('Nuevo caso')}
            </Button>
          </div>
        }
      />
      <div className='flex flex-col gap-6'>
        <div className='flex flex-wrap items-end gap-3'>
          <Input
            className='min-w-48 flex-1'
            size='sm'
            label={t('Buscar caso, referencia o cliente')}
            value={query}
            onValueChange={setQuery}
          />
          {filter('Tipo', kind, setKind, caseKinds)}
          {filter('Estado', status, setStatus, caseStatuses)}
          {filter(
            'Cliente',
            client,
            setClient,
            options(rows.map((c) => c.shipment_snapshot.receiver || '—'))
          )}
          {filter(
            'Responsable de la pérdida',
            responsible,
            setResponsible,
            options(rows.map((c) => c.responsible || 'Sin asignar'))
          )}
          {filter(
            'Esperando a',
            waiting,
            setWaiting,
            options(rows.flatMap((c) => c.waiting_parties))
          )}
        </div>
        {(view === 'summary' || shipment) && (
          <div className='flex flex-wrap items-center justify-end gap-2 text-sm text-default-500'>
            {view === 'summary' && (
              <p className='mr-auto'>
                {t('Temporada')} {scope!.season} · {filtered.length}{' '}
                {t('casos')}
              </p>
            )}
            <div className='flex flex-wrap gap-2'>
              {shipment && (
                <Button
                  variant='light'
                  size='sm'
                  onPress={() => setShipment(null)}
                >
                  {t('Ver todos los despachos')}
                </Button>
              )}
            </div>
          </div>
        )}
        {request.error && (
          <Alert variant='danger' showIcon>
            {request.error}
            <Button variant='flat' onPress={() => void request.reload()}>
              {t('Reintentar')}
            </Button>
          </Alert>
        )}
        {request.loading && !request.data ? (
          <LoadingState label={t('Cargando casos…')} />
        ) : (
          request.data && (
            <>
              {view === 'summary' ? (
                <CasesSummary
                  cases={filtered}
                  onFilter={(field, value) => {
                    ({
                      client: setClient,
                      responsible: setResponsible,
                      kind: setKind,
                      status: setStatus,
                      waiting: setWaiting,
                    })[field](value);
                    setLocation(`/claims/details${window.location.search}`);
                  }}
                />
              ) : (
                <div className='pt-4 overflow-x-auto'>
                  <Table
                    aria-label={t('Casos de despachos')}
                    selectionMode='none'
                    onRowAction={(key) => {
                      const record = filtered.find(
                        (row) => String(row.id) === String(key)
                      );
                      if (record) setSelected({ id: record.id, scope: record });
                    }}
                  >
                    <TableHeader>
                      <TableColumn>{t('Caso / Despacho')}</TableColumn>
                      <TableColumn>{t('Tipo / Estado')}</TableColumn>
                      <TableColumn>{t('Solicitudes pendientes')}</TableColumn>
                      <TableColumn className='text-right'>
                        {t('Kg afectados')}
                      </TableColumn>
                      <TableColumn className='text-right'>
                        {t('Pérdida estimada')}
                      </TableColumn>
                    </TableHeader>
                    <TableBody
                      emptyContent={t('No hay casos con estos filtros.')}
                      items={filtered}
                    >
                      {(c) => (
                        <TableRow
                          key={c.id}
                          className='cursor-pointer transition-colors hover:bg-default-100'
                        >
                          <TableCell>
                            <p className='font-semibold'>{caseLabel(c)}</p>
                            <p>{c.current_reference}</p>
                            <p className='text-xs text-default-500'>
                              {c.shipment_snapshot.receiver}
                            </p>
                          </TableCell>
                          <TableCell>
                            <p className='text-sm'>{t(caseKinds[c.kind])}</p>
                            <StatusChip
                              tone={
                                isCaseOverdue(c, today)
                                  ? 'danger'
                                  : c.status === 'waiting'
                                    ? 'warning'
                                    : c.status === 'closed'
                                      ? 'default'
                                      : 'info'
                              }
                            >
                              {t(caseStatuses[c.status])}
                            </StatusChip>
                          </TableCell>
                          <TableCell>
                            <p className='text-xs text-default-500'>
                              {pendingLabel(c)}
                            </p>
                            {totals.overlapping.has(c.id) && (
                              <p className='text-xs text-default-500'>
                                {t('Carga compartida')}
                              </p>
                            )}
                          </TableCell>
                          <TableCell className='text-right'>
                            {c.items
                              .reduce((n, i) => n + Number(i.kilos), 0)
                              .toLocaleString()}
                          </TableCell>
                          <TableCell className='text-right'>
                            {c.valuation ? (
                              <>
                                {money(c.valuation.amount_usd)}
                                <p className='text-xs text-default-500'>
                                  {t(confidenceLabels[c.valuation.confidence])}
                                </p>
                              </>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )
        )}
      </div>
      {selected && selectedRead.error && (
        <Alert variant='danger'>
          {selectedRead.error}
          <Button variant='light' onPress={() => void selectedRead.reload()}>
            {t('Reintentar')}
          </Button>
        </Alert>
      )}
      {selected && selectedRead.loading && !selectedRead.data && (
        <LoadingState label={t('Cargando caso…')} />
      )}
      {newCase && scope && (
        <NewCaseModal
          scope={scope}
          identity={identity}
          initialShipment={shipment}
          onClose={() => setNewCase(false)}
          onSaved={saved}
        />
      )}
      {selectedRecord && (
        <CaseDetailModal
          key={selectedRecord.id}
          record={selectedRecord}
          identity={identity}
          onClose={() => setSelected(null)}
          onSaved={saved}
          onReload={() => {
            void request.reload();
            void selectedRead.reload();
          }}
        />
      )}
    </PageLayout>
  );
}
